/**
 * ReportPreviewContent - Single Source of Truth for Report View
 * 
 * Used in:
 * 1. Wizard Step 8 (Review) - with action buttons
 * 2. SummaryReportViewPage (admin read-only) 
 * 3. ClientReportViewPage (client portal read-only)
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import BrandedReportHeader from './BrandedReportHeader';
import { formatMetricValue, formatVariance } from '../utils/metricFormatting';
import {
  FileText, Save, Send, CheckCircle2, Wine, UtensilsCrossed, ExternalLink
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area,
} from 'recharts';

// ============================================================================
// Constants
// ============================================================================
const ABBEY_COLORS = {
  primary: '#1e3a5f',
  secondary: '#2d5a87',
  accent: '#4a90c2',
  gold: '#c9a227',
  text: '#1f2937',
  muted: '#6b7280',
  positive: '#059669',
  negative: '#dc2626',
  neutral: '#6b7280'
};

const CURRENCY_SYMBOLS = { 'UK': '£', 'UAE': 'AED ', 'US': '$', 'EU': '€' };

// ============================================================================
// Formatting utilities
// ============================================================================
const formatNumber = (value, currencySymbol = '') => {
  if (value == null || value === '' || isNaN(parseFloat(value))) return 'N/A';
  const num = Math.round(parseFloat(value));
  return num < 0 ? `${currencySymbol}(${Math.abs(num).toLocaleString()})` : `${currencySymbol}${num.toLocaleString()}`;
};

const formatPercent = (value) => {
  if (value == null || value === '' || isNaN(parseFloat(value))) return 'N/A';
  const num = parseFloat(value).toFixed(1);
  return parseFloat(num) < 0 ? `-${Math.abs(parseFloat(num)).toFixed(1)}%` : `${num}%`;
};

const formatCurrency = (value, symbol = '£', decimals = 0) => {
  if (value == null || value === '' || isNaN(parseFloat(value))) return 'N/A';
  const num = parseFloat(value);
  if (decimals > 0) {
    const rounded = parseFloat(num.toFixed(decimals));
    return rounded < 0
      ? `-${symbol}${Math.abs(rounded).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
      : `${symbol}${rounded.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }
  const rounded = Math.round(num);
  return rounded < 0 ? `-${symbol}${Math.abs(rounded).toLocaleString()}` : `${symbol}${rounded.toLocaleString()}`;
};

const formatDateFn = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const getScopeInfo = (scope) => {
  if (scope === 'FOOD') return { label: 'Food', color: 'text-orange-600', icon: <UtensilsCrossed className="h-5 w-5" /> };
  if (scope === 'SHOP') return { label: 'Shop', color: 'text-purple-600', icon: <UtensilsCrossed className="h-5 w-5" /> };
  return { label: 'Beverage', color: 'text-blue-600', icon: <Wine className="h-5 w-5" /> };
};

// ============================================================================
// Sub-components
// ============================================================================

const MiniTrendChart = ({ data, dataKey, color, unit = '', currencySymbol = '£', tightScale = false }) => {
  if (!data || data.length < 2) return null;

  const values = data.map(d => parseFloat(d[dataKey]) || 0).filter(v => !isNaN(v));
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal;
  
  let domain;
  if (tightScale && range < 5) {
    const padding = Math.max(range * 0.5, 1);
    domain = [Math.max(0, minVal - padding), maxVal + padding];
  } else {
    domain = ['auto', 'auto'];
  }

  return (
    <div className="h-40 mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 10, left: 5, bottom: 25 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 8, fill: ABBEY_COLORS.muted }}
            tickLine={false} axisLine={false}
            angle={-45} textAnchor="end" interval={0} height={40}
          />
          <YAxis 
            tick={{ fontSize: 8, fill: ABBEY_COLORS.muted }}
            tickLine={false} axisLine={false} width={40} domain={domain}
            tickFormatter={(value) => {
              if (unit === 'currency') {
                const absVal = Math.abs(value);
                if (absVal >= 1000) return `${currencySymbol}${(value / 1000).toFixed(1)}k`;
                return `${currencySymbol}${Math.round(value)}`;
              }
              if (unit === '%') return `${value.toFixed(0)}%`;
              return value;
            }}
          />
          <Tooltip 
            formatter={(value) => {
              const num = parseFloat(value || 0);
              if (unit === 'currency') return [num < 0 ? `${currencySymbol}(${Math.abs(Math.round(num)).toLocaleString()})` : `${currencySymbol}${Math.round(num).toLocaleString()}`];
              if (unit === '%') return [num < 0 ? `(${Math.abs(num).toFixed(1)}%)` : `${num.toFixed(1)}%`];
              return [Math.round(num).toLocaleString()];
            }}
            labelStyle={{ color: ABBEY_COLORS.text, fontSize: 11 }}
            contentStyle={{ borderRadius: '6px', border: `1px solid ${color}`, fontSize: 11, padding: '4px 8px' }}
            wrapperStyle={{ zIndex: 50 }}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2}
            dot={{ fill: color, strokeWidth: 1, r: 3 }} activeDot={{ r: 4, fill: color }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const KPITileWithChart = ({ title, value, unit, color, bgColor, trendData, dataKey, chartColor, currencySymbol = '£', tightScale = false }) => {
  const displayValue = (() => {
    if (value === '' || value === null || value === undefined) return '—';
    if (unit === 'currency') return formatCurrency(value, currencySymbol);
    if (unit === '%') return formatPercent(value);
    return formatNumber(value);
  })();
  
  return (
    <div className={`${bgColor} p-3 rounded-lg print:break-inside-avoid`}>
      <p className={`text-xs uppercase font-medium ${color}`}>{title}</p>
      <p className={`text-xl font-bold ${color} mt-1`}>{displayValue}</p>
      {trendData && trendData.length >= 2 && (
        <MiniTrendChart data={trendData} dataKey={dataKey} color={chartColor}
          unit={unit} currencySymbol={currencySymbol} tightScale={tightScale}
        />
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * @param {Object} props
 * @param {Object} props.report - The report object (from backend)
 * @param {Object} props.sectionsJson - The sections_json data (if different from report.sections_json)
 * @param {Object} props.settings - Organisation settings (for thresholds, region)
 * @param {boolean} props.showExportPdf - Show Export PDF button
 * @param {Function} props.onExportPdf - Export PDF handler
 * @param {boolean} props.isExporting - Export in progress
 * @param {boolean} props.showActions - Show Save Draft / Publish buttons (wizard only)
 * @param {Function} props.onSave - Save handler (wizard)
 * @param {Function} props.onPublish - Publish handler (wizard)
 * @param {boolean} props.saving - Saving in progress
 * @param {boolean} props.publishing - Publishing in progress  
 * @param {boolean} props.hasUnsavedChanges - Has unsaved changes (wizard)
 * @param {boolean} props.canEdit - Can edit (wizard)
 */
const ReportPreviewContent = ({
  report,
  sectionsJson,
  settings,
  showExportPdf = false,
  onExportPdf,
  isExporting = false,
  showActions = false,
  onSave,
  onPublish,
  saving = false,
  publishing = false,
  hasUnsavedChanges = false,
  canEdit = false,
  capturedMetrics = [],
  metricsTemplate = null
}) => {
  const sections = report ? (sectionsJson || report?.sections_json || {}) : {};
  const scopeInfo = getScopeInfo(report?.scope);
  
  const currencySymbol = useMemo(() => {
    const region = settings?.default_region || 'UK';
    return CURRENCY_SYMBOLS[region] || '£';
  }, [settings?.default_region]);

  const reviewMetrics = sections.metrics || {};
  const reviewVariance = sections.variance || { shortages: [], surpluses: [] };
  // Uncategorised Stocktake allowances are shown as locked lines and counted once.
  const reviewAllowances = useMemo(() => [
    ...(sections.allowances || []),
    ...(sections.allowances_uncategorised || []).map(r => ({ ...r, uncategorised: true })),
  ], [sections.allowances, sections.allowances_uncategorised]);
  const reviewManagement = sections.management || [];
  const reviewAdjustments = sections.adjustments || [];
  const reviewObservations = sections.observations || {};
  const reviewAbvChecks = sections.abv_checks || [];
  const reviewFoodAllowances = sections.food_allowances || [];

  const isFoodScope = report?.scope === 'FOOD';

  const allowancesSubtotal = useMemo(() => ({
    retail: reviewAllowances.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
    cost: reviewAllowances.reduce((sum, item) => sum + (parseFloat(item.at_cost) || 0), 0)
  }), [reviewAllowances]);

  const managementSubtotal = useMemo(() => ({
    retail: reviewManagement.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
    cost: reviewManagement.reduce((sum, item) => sum + (parseFloat(item.at_cost) || 0), 0)
  }), [reviewManagement]);

  const adjustmentsSubtotal = useMemo(() => ({
    retail: reviewAdjustments.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
    cost: 0
  }), [reviewAdjustments]);

  const foodAllowancesTotal = useMemo(() => 
    reviewFoodAllowances.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
    [reviewFoodAllowances]
  );

  // Build trend chart data from history + current period
  const trendChartData = useMemo(() => {
    const historyItems = sections.history?.items || [];
    const dataPoints = historyItems
      .filter(item => item.audit_date)
      .map(item => ({
        date: formatDateFn(item.audit_date),
        sortDate: new Date(item.audit_date).getTime(),
        gp_percent: parseFloat(item.gp_percent) || 0,
        gp_before_allowances_percent: parseFloat(item.gp_before_allowances_percent) || 0,
        stockholding_value: parseFloat(item.stockholding_value) || 0,
        stockholding_days: parseFloat(item.stockholding_days) || 0,
        sales_for_period: parseFloat(item.sales_for_period) || 0,
        cos_percent: parseFloat(item.cos_percent) || 0,
        wastage_value: parseFloat(item.wastage_value) || 0,
        stocktake_result_value: parseFloat(item.stocktake_result_value) || 0,
        stocktake_result_percent: parseFloat(item.stocktake_result_percent) || 0,
        exit_meeting_attended: item.exit_meeting_attended
      }));

    if (report?.audit_date && (reviewMetrics.gp_percent || reviewMetrics.stockholding_value || reviewMetrics.wastage_value || reviewMetrics.stocktake_result_value)) {
      dataPoints.push({
        date: formatDateFn(report.audit_date),
        sortDate: new Date(report.audit_date).getTime(),
        gp_percent: parseFloat(reviewMetrics.gp_percent) || 0,
        gp_before_allowances_percent: parseFloat(reviewMetrics.gp_before_allowances_percent) || 0,
        stockholding_value: parseFloat(reviewMetrics.stockholding_value) || 0,
        stockholding_days: parseFloat(reviewMetrics.stockholding_days) || 0,
        sales_for_period: parseFloat(reviewMetrics.sales_for_period) || 0,
        cos_percent: parseFloat(reviewMetrics.cos_percent) || 0,
        wastage_value: parseFloat(reviewMetrics.wastage_value) || 0,
        stocktake_result_value: parseFloat(reviewMetrics.stocktake_result_value) || 0,
        stocktake_result_percent: parseFloat(reviewMetrics.stocktake_result_percent) || 0,
        exit_meeting_attended: reviewMetrics.exit_meeting_attended,
        isCurrent: true
      });
    }

    dataPoints.sort((a, b) => a.sortDate - b.sortDate);
    return dataPoints;
  }, [sections.history, reviewMetrics, report?.audit_date]);

  // Get thresholds - check for custom per-client/site thresholds first
  const thresholds = settings?.summary_report_thresholds || {};
  const customThresholds = thresholds.custom_thresholds || [];
  
  // Match custom threshold: check site_id first, then parent_client_id/independent_client_id
  const siteId = report?.site_id;
  const clientId = report?.parent_client_id || report?.independent_client_id;
  
  const matchedCustom = customThresholds.find(ct => {
    if (ct.site_ids?.length > 0 && siteId && ct.site_ids.includes(siteId)) return true;
    if (ct.client_ids?.length > 0 && clientId && ct.client_ids.includes(clientId)) return true;
    return false;
  });
  
  // Use custom thresholds if matched, otherwise global from settings
  const stockResultGreenLower = matchedCustom?.threshold?.green_lower ?? thresholds.stock_result_green_lower ?? -2.0;
  const stockResultGreenUpper = matchedCustom?.threshold?.green_upper ?? thresholds.stock_result_green_upper ?? 0.5;
  const cosGreenVariance = thresholds.cos_variance_green ?? 0.5;
  const cosAmberVariance = thresholds.cos_variance_amber ?? 0.5;
  
  // Determine stock result colour considering custom green range
  const getStockResultStatus = (val) => {
    if (isNaN(val)) return 'neutral';
    
    // Custom threshold with explicit green/amber/red ranges
    if (matchedCustom?.threshold) {
      const ct = matchedCustom.threshold;
      const redLower = ct.red_lower ?? -999;
      
      // Check red first
      if (redLower != null && val < redLower) return 'red';
      if (ct.red_upper != null && val > ct.red_upper) return 'red';
      
      // Check green range
      if (ct.green_lower != null && ct.green_upper != null) {
        if (val >= ct.green_lower && val <= ct.green_upper) return 'green';
      }
      
      return 'amber';
    }
    
    // Universal thresholds: green range, everything else red (no amber)
    if (val >= stockResultGreenLower && val <= stockResultGreenUpper) return 'green';
    return 'red';
  };

  // Traffic light cell background
  const getCellBg = (status) => {
    if (status === 'green') return 'bg-green-100 text-green-800 font-semibold';
    if (status === 'amber') return 'bg-amber-100 text-amber-800 font-semibold';
    if (status === 'red') return 'bg-red-100 text-red-800 font-semibold';
    return 'bg-slate-50 text-slate-600';
  };

  // Results breakdown data
  const historyItems = sections.history?.items || [];
  const lastPeriod = historyItems.length > 0 
    ? [...historyItems].sort((a, b) => new Date(b.audit_date || 0) - new Date(a.audit_date || 0))[0]
    : null;

  // iter441 — Resolve period_days for Sales per-day normalisation. Reads
  // persisted `stock_period_days` when present, otherwise derives from the
  // gap between consecutive audit_dates in the chronological chain. Returns
  // null when neither path is resolvable (caller falls back to absolute cmp).
  const ascHistory = useMemo(() =>
    [...historyItems].sort((a, b) => new Date(a.audit_date || 0) - new Date(b.audit_date || 0)),
    [historyItems]
  );
  const resolvePeriodDays = (item, idx) => {
    if (!item) return null;
    // 1. Persisted stock_period_days (authoritative when present).
    const stored = item.stock_period_days;
    const storedNum = stored != null && stored !== '' ? parseFloat(stored) : null;
    if (storedNum != null && !isNaN(storedNum) && storedNum > 0) return storedNum;
    // 2. iter516 — Derive from this item's own audit_date -
    //    previous_audit_date. Persisted directly on the history item,
    //    so it's the true stock period for THAT report — not a
    //    fabricated gap from an arbitrary neighbouring audit.
    //    (Conrad London St. James case: 28 Jul 2026 report had
    //    previous_audit_date=30 Jun 2026 → 35 days, correct. The old
    //    neighbour-gap fallback returned 27 May → 28 Jul = 62 days,
    //    driving £215,835/62 = £3,481/day instead of £6,167/day.)
    const prev = item.previous_audit_date;
    if (item.audit_date && prev) {
      try {
        const thisD = new Date(item.audit_date);
        const priorD = new Date(prev);
        const gap = Math.round((thisD - priorD) / (1000 * 60 * 60 * 24));
        if (gap >= 1 && gap <= 365) return gap;
      } catch { /* fall through */ }
    }
    // 3. Give up — do NOT guess from neighbouring history items. That
    //    fallback caused the bug above whenever the chain had a wider
    //    gap than the report's actual stock period (missed audits,
    //    scope changes, or off-cycle interim audits).
    return null;
  };
  const lastIdx = lastPeriod ? ascHistory.findIndex(h => h.audit_date === lastPeriod.audit_date) : -1;
  const _secondLastForDays = ascHistory.length > 1 && lastIdx > 0 ? ascHistory[lastIdx - 1] : null;
  const secondLastIdx = _secondLastForDays ? ascHistory.findIndex(h => h.audit_date === _secondLastForDays.audit_date) : -1;

  const salesCurrentDays = (() => {
    const r = report?.stock_period_days;
    const v = r != null && r !== '' ? parseFloat(r) : null;
    return v != null && !isNaN(v) && v > 0 ? v : null;
  })();
  const salesLastDays = resolvePeriodDays(lastPeriod, lastIdx);
  const salesSecondLastDays = resolvePeriodDays(_secondLastForDays, secondLastIdx);
  const _perDay = (v, d) => {
    const value = v != null && v !== '' ? parseFloat(v) : null;
    if (value == null || isNaN(value) || !d || d <= 0) return null;
    return value / d;
  };
  // A field can arrive as null, undefined, '' (blank source) — treat all
  // three as "absent" rather than letting parseFloat('') turn into NaN
  // and silently colour a rating that was never actually calculated.
  const _numOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };
  const salesCurrentPerDay = _perDay(reviewMetrics.sales_for_period, salesCurrentDays);
  const salesLastPerDay = _perDay(lastPeriod?.sales_for_period, salesLastDays);
  const salesSecondLastPerDay = _perDay(_secondLastForDays?.sales_for_period, salesSecondLastDays);

  const resultsData = useMemo(() => {
    const secondLast = historyItems.length > 1
      ? [...historyItems].sort((a, b) => new Date(b.audit_date || 0) - new Date(a.audit_date || 0))[1]
      : null;

    return [
      // Stock Result — Beverages only (not relevant for Food scope)
      ...(report?.scope !== 'FOOD' ? [{
        metric: 'Stock Result',
        thisPeriod: reviewMetrics.stocktake_result_percent,
        lastPeriod: lastPeriod?.stocktake_result_percent,
        thisValue: reviewMetrics.stocktake_result_value,
        lastValue: lastPeriod?.stocktake_result_value,
        format: (v, _target, row, isLast) => {
          if (v == null) return 'N/A';
          const val = isLast ? row?.lastValue : row?.thisValue;
          const pctStr = formatPercent(v);
          const amtStr = val != null ? formatCurrency(val, currencySymbol) : '';
          return amtStr ? `${amtStr} ${pctStr}` : pctStr;
        },
        status: (() => {
          const val = parseFloat(reviewMetrics.stocktake_result_percent);
          return getStockResultStatus(val);
        })(),
        lastStatus: (() => {
          const val = parseFloat(lastPeriod?.stocktake_result_percent);
          return getStockResultStatus(val);
        })()
      }] : []),
      {
        metric: 'COS % vs Budget',
        thisPeriod: reviewMetrics.cos_percent,
        lastPeriod: lastPeriod?.cos_percent,
        target: report?.site_budget_cos_pct,
        format: (v, target) => {
          const num = _numOrNull(v);
          if (num == null) return 'N/A';
          const variance = target != null ? num - target : null;
          if (variance != null) {
            const varStr = variance < 0 ? `(${Math.abs(variance).toFixed(1)}%)` : `${variance.toFixed(1)}%`;
            return `${num.toFixed(1)}% ${varStr}`;
          }
          return `${num.toFixed(1)}%`;
        },
        status: (() => {
          const budget = report?.site_budget_cos_pct;
          const current = _numOrNull(reviewMetrics.cos_percent);
          if (budget == null || current == null) return 'neutral';
          const variance = current - budget;
          // iter464 — Any COS at or under budget is a favourable outcome and
          // must render GREEN. AMBER only applies to over-budget variances
          // that stay within tolerance; RED applies beyond tolerance.
          if (variance <= 0) return 'green';
          if (variance <= cosAmberVariance) return 'amber';
          return 'red';
        })(),
        lastStatus: (() => {
          const budget = report?.site_budget_cos_pct;
          const last = _numOrNull(lastPeriod?.cos_percent);
          if (budget == null || last == null) return 'neutral';
          const variance = last - budget;
          if (variance <= 0) return 'green';
          if (variance <= cosAmberVariance) return 'amber';
          return 'red';
        })()
      },
      {
        metric: 'Stockholding Value',
        thisPeriod: reviewMetrics.stockholding_value,
        lastPeriod: lastPeriod?.stockholding_value,
        format: (v) => v != null ? formatCurrency(v, currencySymbol) : 'N/A',
        status: (() => {
          const current = parseFloat(reviewMetrics.stockholding_value);
          const last = parseFloat(lastPeriod?.stockholding_value);
          if (isNaN(current) || isNaN(last)) return 'neutral';
          if (current > last) return 'red';
          if (current < last) return 'green';
          return 'amber';
        })(),
        lastStatus: (() => {
          if (!secondLast) return 'neutral';
          const last = parseFloat(lastPeriod?.stockholding_value);
          const prev = parseFloat(secondLast?.stockholding_value);
          if (isNaN(last) || isNaN(prev)) return 'neutral';
          if (last > prev) return 'red';
          if (last < prev) return 'green';
          return 'amber';
        })()
      },
      {
        metric: 'Stockholding Days vs Last',
        thisPeriod: reviewMetrics.stockholding_days,
        lastPeriod: lastPeriod?.stockholding_days,
        format: (v) => v != null ? `${Math.round(parseFloat(v))} Days` : 'N/A',
        status: (() => {
          const current = parseInt(reviewMetrics.stockholding_days);
          const last = parseInt(lastPeriod?.stockholding_days);
          if (isNaN(current) || isNaN(last)) return 'neutral';
          if (current > last) return 'red';
          if (current < last) return 'green';
          return 'amber';
        })(),
        lastStatus: (() => {
          if (!secondLast) return 'neutral';
          const last = parseInt(lastPeriod?.stockholding_days);
          const prev = parseInt(secondLast?.stockholding_days);
          if (isNaN(last) || isNaN(prev)) return 'neutral';
          if (last > prev) return 'red';
          if (last < prev) return 'green';
          return 'amber';
        })()
      },
      {
        metric: 'Stockholding Days vs Target',
        thisPeriod: reviewMetrics.stockholding_days,
        lastPeriod: lastPeriod?.stockholding_days,
        target: report?.site_target_stockholding_days,
        format: (v, target) => {
          // iter515 — Show ONLY the variance vs Target here; the raw
          // days figure is already reported one row above ("Stockholding
          // Days vs Last"), so repeating it is noise.
          if (v == null) return 'N/A';
          const days = Math.round(parseFloat(v));
          if (target == null) return 'N/A';
          const diff = days - target;
          if (diff === 0) return 'On Target';
          return diff < 0 ? `(${Math.abs(diff)}) Days` : `+${diff} Days`;
        },
        status: (() => {
          const target = report?.site_target_stockholding_days;
          if (target == null || reviewMetrics.stockholding_days == null) return 'neutral';
          const current = parseInt(reviewMetrics.stockholding_days);
          // 2026-04-24 (iter321) — ON or BETTER than target is GREEN
          // (lower stockholding days = better cash utilisation). Only
          // exceeding the target turns the metric red.
          if (current > target) return 'red';
          return 'green';
        })(),
        lastStatus: (() => {
          const target = report?.site_target_stockholding_days;
          if (target == null || !lastPeriod?.stockholding_days) return 'neutral';
          const lastDays = parseInt(lastPeriod.stockholding_days);
          if (isNaN(lastDays)) return 'neutral';
          if (lastDays > target) return 'red';
          return 'green';
        })()
      },
      {
        metric: 'Sales for Period',
        note: 'Compared per-day vs last period',
        thisPeriod: reviewMetrics.sales_for_period,
        lastPeriod: lastPeriod?.sales_for_period,
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (v, _t, row, isLast) => {
          if (v == null || v === '') return 'N/A';
          const amount = formatCurrency(v, currencySymbol);
          // iter441 — show per-day subtitle when period days resolvable so
          // the operator sees the period-normalised comparison driving the
          // traffic light.
          const days = isLast ? row?.lastPeriodDays : row?.currentPeriodDays;
          const perDay = isLast ? row?.lastPerDay : row?.currentPerDay;
          if (days && perDay != null) {
            return (
              <span>
                {amount}
                <br />
                <span className="text-[10px] italic text-slate-400">
                  {formatCurrency(perDay, currencySymbol)}/day · {days} days
                </span>
              </span>
            );
          }
          return amount;
        },
        currentPeriodDays: salesCurrentDays,
        lastPeriodDays: salesLastDays,
        currentPerDay: salesCurrentPerDay,
        lastPerDay: salesLastPerDay,
        status: (() => {
          // iter441 — period-normalised comparison. Fall back to absolute
          // when period days cannot be resolved on either side.
          if (salesCurrentPerDay != null && salesLastPerDay != null) {
            if (salesCurrentPerDay < salesLastPerDay) return 'red';
            if (salesCurrentPerDay > salesLastPerDay) return 'green';
            return 'amber';
          }
          const current = parseFloat(reviewMetrics.sales_for_period);
          const last = parseFloat(lastPeriod?.sales_for_period);
          if (isNaN(current) || isNaN(last)) return 'neutral';
          if (current < last) return 'red';
          if (current > last) return 'green';
          return 'amber';
        })(),
        lastStatus: (() => {
          if (!secondLast) return 'neutral';
          if (salesLastPerDay != null && salesSecondLastPerDay != null) {
            if (salesLastPerDay < salesSecondLastPerDay) return 'red';
            if (salesLastPerDay > salesSecondLastPerDay) return 'green';
            return 'amber';
          }
          const last = parseFloat(lastPeriod?.sales_for_period);
          const prev = parseFloat(secondLast?.sales_for_period);
          if (isNaN(last) || isNaN(prev)) return 'neutral';
          if (last < prev) return 'red';
          if (last > prev) return 'green';
          return 'amber';
        })()
      },
      // Wastage — Food scope (lower is better)
      ...(report?.scope === 'FOOD' ? [{
        metric: 'Wastage',
        thisPeriod: reviewMetrics.wastage_value,
        lastPeriod: lastPeriod?.wastage_value,
        format: (v) => v != null ? formatCurrency(v, currencySymbol) : 'N/A',
        status: (() => {
          const current = parseFloat(reviewMetrics.wastage_value);
          const last = parseFloat(lastPeriod?.wastage_value);
          if (isNaN(current) || isNaN(last)) return 'neutral';
          if (current > last) return 'red';
          if (current < last) return 'green';
          return 'amber';
        })(),
        lastStatus: (() => {
          if (!secondLast) return 'neutral';
          const last = parseFloat(lastPeriod?.wastage_value);
          const prev = parseFloat(secondLast?.wastage_value);
          if (isNaN(last) || isNaN(prev)) return 'neutral';
          if (last > prev) return 'red';
          if (last < prev) return 'green';
          return 'amber';
        })()
      }] : []),
      // Yield % — Beverages only (absolute threshold colour coding)
      ...(report?.scope !== 'FOOD' ? [{
        metric: 'Yield %',
        note: '≥96% green · 93–96% amber · <93% red',
        thisPeriod: reviewMetrics.yield_percent,
        lastPeriod: lastPeriod?.yield_percent,
        format: (v) => v != null && v !== '' ? `${parseFloat(v).toFixed(1)}%` : 'N/A',
        status: (() => {
          const current = parseFloat(reviewMetrics.yield_percent);
          if (isNaN(current)) return 'neutral';
          if (current >= 96) return 'green';
          if (current >= 93) return 'amber';
          return 'red';
        })(),
        lastStatus: (() => {
          const last = parseFloat(lastPeriod?.yield_percent);
          if (isNaN(last)) return 'neutral';
          if (last >= 96) return 'green';
          if (last >= 93) return 'amber';
          return 'red';
        })()
      }] : []),
      {
        metric: 'Exit Meeting Attendance',
        thisPeriod: reviewMetrics.exit_meeting_attended,
        lastPeriod: lastPeriod?.exit_meeting_attended,
        format: (v) => v === true ? 'Attended' : v === false ? 'Not Attended' : 'N/A',
        status: reviewMetrics.exit_meeting_attended === true ? 'green' : reviewMetrics.exit_meeting_attended === false ? 'red' : 'neutral',
        lastStatus: lastPeriod?.exit_meeting_attended === true ? 'green' : lastPeriod?.exit_meeting_attended === false ? 'red' : 'neutral'
      }
    ];
  }, [reviewMetrics, lastPeriod, historyItems, report, currencySymbol, stockResultGreenLower, stockResultGreenUpper, cosGreenVariance, cosAmberVariance]);

  if (!report) return <div className="p-4 text-center text-muted-foreground">Loading report...</div>;

  return (
    <div className="space-y-6 print:text-sm" data-testid="report-preview-content">
      {/* Print Styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
      
      {/* Branded Header */}
      <BrandedReportHeader 
        report={report} 
        scopeInfo={scopeInfo}
        showExportPdf={showExportPdf}
        onExportPdf={onExportPdf}
        isExporting={isExporting}
      />
      
      {/* Client Report Metrics (from template) - always show for qualifying clients */}
      {metricsTemplate && (
        <div className="print:break-inside-avoid mb-6" data-testid="client-metrics-section">
          <h3 className="font-semibold border-b pb-1 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            {metricsTemplate.title || 'Client Metrics'}
          </h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr style={{ backgroundColor: ABBEY_COLORS.primary, color: '#fff' }}>
                <th className="text-left py-2 px-3 font-medium">Metric</th>
                <th className="text-center py-2 px-3 font-medium">This Period</th>
                <th className="text-center py-2 px-3 font-medium">Last Period</th>
                <th className="text-center py-2 px-3 font-medium">Variance</th>
                <th className="text-left py-2 px-3 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {capturedMetrics.length > 0 ? capturedMetrics.map((m, idx) => {
                const mType = m.metric_type_snapshot || 'PERCENTAGE';
                const variance = (m.this_period_value != null && m.last_period_value != null)
                  ? m.this_period_value - m.last_period_value : null;
                const varianceStr = formatVariance(m.this_period_value, m.last_period_value, mType);
                return (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td className="py-2 px-3 font-medium">{m.metric_label_snapshot}</td>
                    <td className="py-2 px-3 text-center">{formatMetricValue(m.this_period_value, mType)}</td>
                    <td className="py-2 px-3 text-center">{formatMetricValue(m.last_period_value, mType)}</td>
                    <td className="py-2 px-3 text-center">
                      {varianceStr ? (
                        <span style={{ color: variance > 0 ? ABBEY_COLORS.positive : variance < 0 ? ABBEY_COLORS.negative : ABBEY_COLORS.muted, fontWeight: 600 }}>
                          {varianceStr}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3 text-sm text-slate-600">{m.note || '—'}</td>
                  </tr>
                );
              }) : (
                <tr className="bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td colSpan={5} className="py-3 px-3 text-center text-muted-foreground text-sm italic">
                    No metrics captured for this period — all values nil
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Key Performance Indicators with Trend Charts */}
      <div className="print:break-inside-avoid">
        <h3 className="font-semibold border-b pb-1 mb-2" style={{ color: ABBEY_COLORS.primary }}>
          Key Performance Indicators
        </h3>
        <div className={`grid grid-cols-2 ${isFoodScope ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-2 mb-3`}>
          <KPITileWithChart
            title="Stockholding"
            value={reviewMetrics.stockholding_value}
            unit="currency"
            color="text-blue-700"
            bgColor="bg-blue-50"
            trendData={trendChartData}
            dataKey="stockholding_value"
            chartColor={ABBEY_COLORS.accent}
            currencySymbol={currencySymbol}
          />
          {/* Food scope: always show GP% Before Allowances line graph (Wastage is in Results Breakdown table) */}
          {/* Beverage scope: show Wastage line graph */}
          {isFoodScope ? (
            <KPITileWithChart
              title="GP % Before Allowances"
              value={reviewMetrics.gp_before_allowances_percent}
              unit="%"
              color="text-amber-700"
              bgColor="bg-amber-50"
              trendData={trendChartData}
              dataKey="gp_before_allowances_percent"
              chartColor={ABBEY_COLORS.gold}
              tightScale={true}
            />
          ) : (
            <KPITileWithChart
              title="Wastage"
              value={reviewMetrics.wastage_value}
              unit="currency"
              color="text-orange-700"
              bgColor="bg-orange-50"
              trendData={trendChartData}
              dataKey="wastage_value"
              chartColor={ABBEY_COLORS.gold}
              currencySymbol={currencySymbol}
            />
          )}
          <KPITileWithChart
            title={report?.scope === 'FOOD' ? 'GP % After Allowances' : 'Gross Profit %'}
            value={reviewMetrics.gp_percent}
            unit="%"
            color="text-green-700"
            bgColor="bg-green-50"
            trendData={trendChartData}
            dataKey="gp_percent"
            chartColor={ABBEY_COLORS.secondary}
            tightScale={true}
          />
          {/* Result - Beverages only (not relevant for Food scope) */}
          {!isFoodScope && (
          <div className={`p-3 rounded-lg ${(() => {
            const stockResultPct = parseFloat(reviewMetrics.stocktake_result_percent) || 0;
            // Use green range from org settings: within range = green, outside = red
            if (stockResultPct >= stockResultGreenLower && stockResultPct <= stockResultGreenUpper) return 'bg-green-50';
            return 'bg-red-50';
          })()}`}>
            <p className="text-xs uppercase font-medium text-slate-700">Result</p>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="text-xl font-bold text-slate-800">{formatCurrency(reviewMetrics.stocktake_result_value, currencySymbol)}</p>
              <p className="text-sm font-semibold text-slate-600">{formatPercent(reviewMetrics.stocktake_result_percent)}</p>
            </div>
            <MiniTrendChart 
              data={trendChartData} 
              dataKey="stocktake_result_percent" 
              color={ABBEY_COLORS.primary}
              unit="%"
              tightScale={true}
            />
          </div>
          )}
        </div>
      </div>
      
      {/* Results Breakdown */}
      <div className="print:break-inside-avoid" data-testid="results-breakdown-section">
        <h3 className="font-semibold border-b pb-1 mb-2" style={{ color: ABBEY_COLORS.primary }}>
          Results Breakdown
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-700 text-white">
                <th className="px-3 py-2 text-left font-semibold">Metric</th>
                <th className="px-3 py-2 text-center font-semibold">This Period</th>
                <th className="px-3 py-2 text-center font-semibold">Last Period</th>
              </tr>
            </thead>
            <tbody>
              {resultsData.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-200">
                  <td className="px-3 py-2 font-medium bg-slate-50 text-xs">
                    {row.metric}
                    {row.note && <span className="block text-[10px] text-slate-400 font-normal italic">{row.note}</span>}
                  </td>
                  <td className={`px-3 py-2 text-center text-xs ${getCellBg(row.status)}`}>
                    {row.format(row.thisPeriod, row.target, row, false)}
                  </td>
                  <td className={`px-3 py-2 text-center text-xs ${getCellBg(row.lastStatus || 'neutral')}`}>
                    {row.lastPeriod != null ? row.format(row.lastPeriod, row.target, row, true) : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Warnings */}
        <div className="mt-2 text-xs text-amber-600 space-y-0.5">
          {!lastPeriod && <p>* No previous audit data available.</p>}
          {report?.site_budget_cos_pct == null && <p>* COS Budget % not configured.</p>}
          {report?.site_target_stockholding_days == null && <p>* Stockholding Days Target not configured.</p>}
        </div>
        
        {/* Key */}
        <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200 text-xs text-slate-600">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold text-slate-700">Key:</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>Favourable</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>Caution</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>Unfavourable</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span>No data</span>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-500 italic">
            Stock Result: GREEN if between {stockResultGreenLower}% and {stockResultGreenUpper}%{matchedCustom ? ' (custom criteria applied)' : ''}.
            COS vs Budget: GREEN if actual is at or below budget. AMBER if over budget by up to {cosAmberVariance}%. RED if over budget by more than {cosAmberVariance}%.
            Comparison metrics: GREEN if improved vs previous period.
          </p>
        </div>
      </div>
      
      {/* PLU Variance Analysis — Beverages only */}
      {!isFoodScope && (reviewVariance.shortages?.length > 0 || reviewVariance.surpluses?.length > 0) && (
        <div className="print:break-inside-avoid">
          <h3 className="font-semibold border-b pb-2 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            PLU Variance Analysis
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reviewVariance.shortages?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-600 mb-2">Top Shortages</h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Item</th>
                      <th className="px-2 py-1 text-center">Qty</th>
                      <th className="px-2 py-1 text-center">Measure</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">Price</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">Amount</th>
                      <th className="px-2 py-1 text-left">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewVariance.shortages.map((item, idx) => {
                      const legacy = !item.quantity && !item.price && item.at_retail;
                      return (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">{item.item_name || '-'}</td>
                          {legacy ? (
                            <td className="px-2 py-1 text-center text-xs text-muted-foreground" colSpan={3}>—</td>
                          ) : (
                            <>
                              <td className="px-2 py-1 text-center">{item.quantity || '-'}</td>
                              <td className="px-2 py-1 text-center text-xs">{item.measure || '-'}</td>
                              <td className="px-2 py-1 text-right whitespace-nowrap">{item.price ? formatCurrency(item.price, currencySymbol, 2) : '-'}</td>
                            </>
                          )}
                          <td className="px-2 py-1 text-right font-medium whitespace-nowrap">{formatCurrency(item.at_retail, currencySymbol, 2)}</td>
                          <td className="px-2 py-1 text-xs text-muted-foreground">{item.optional_note || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {reviewVariance.surpluses?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-600 mb-2">Top Surpluses</h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Item</th>
                      <th className="px-2 py-1 text-center">Qty</th>
                      <th className="px-2 py-1 text-center">Measure</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">Price</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">Amount</th>
                      <th className="px-2 py-1 text-left">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewVariance.surpluses.map((item, idx) => {
                      const legacy = !item.quantity && !item.price && item.at_retail;
                      return (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">{item.item_name || '-'}</td>
                          {legacy ? (
                            <td className="px-2 py-1 text-center text-xs text-muted-foreground" colSpan={3}>—</td>
                          ) : (
                            <>
                              <td className="px-2 py-1 text-center">{item.quantity || '-'}</td>
                              <td className="px-2 py-1 text-center text-xs">{item.measure || '-'}</td>
                              <td className="px-2 py-1 text-right whitespace-nowrap">{item.price ? formatCurrency(item.price, currencySymbol, 2) : '-'}</td>
                            </>
                          )}
                          <td className="px-2 py-1 text-right font-medium whitespace-nowrap">{formatCurrency(item.at_retail, currencySymbol, 2)}</td>
                          <td className="px-2 py-1 text-xs text-muted-foreground">{item.optional_note || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Allowances & Management — Beverages only */}
      {!isFoodScope && (reviewAllowances.length > 0 || reviewManagement.length > 0 || reviewAdjustments.length > 0) && (
        <div className="print:break-inside-avoid">
          <h3 className="font-semibold border-b pb-2 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            Allowances, Management & Adjustments
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reviewAllowances.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-blue-600 mb-2">Allowances</h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-slate-100">
                    <tr><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Retail Selling Price</th></tr>
                  </thead>
                  <tbody>
                    {reviewAllowances.map((item, idx) => (
                      <tr key={idx} className="border-t" data-testid={item.uncategorised ? `review-uncategorised-allowance-${idx}` : undefined}>
                        <td className="px-2 py-1">
                          {item.description || '-'}
                        </td>
                        <td className="px-2 py-1 text-right">{formatCurrency(item.at_retail, currencySymbol)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-medium">
                    <tr className="border-t">
                      <td className="px-2 py-1 text-right">Total:</td>
                      <td className="px-2 py-1 text-right">{formatCurrency(allowancesSubtotal.retail, currencySymbol)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {reviewManagement.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-purple-600 mb-2">Management</h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-slate-100">
                    <tr><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Retail</th><th className="px-2 py-1 text-right">Cost</th></tr>
                  </thead>
                  <tbody>
                    {reviewManagement.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">{item.description || '-'}</td>
                        <td className="px-2 py-1 text-right">{formatCurrency(item.at_retail, currencySymbol)}</td>
                        <td className="px-2 py-1 text-right">{formatCurrency(item.at_cost, currencySymbol)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-medium">
                    <tr className="border-t">
                      <td className="px-2 py-1 text-right">Total:</td>
                      <td className="px-2 py-1 text-right">{formatCurrency(managementSubtotal.retail, currencySymbol)}</td>
                      <td className="px-2 py-1 text-right">{formatCurrency(managementSubtotal.cost, currencySymbol)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {reviewAdjustments.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-amber-600 mb-2">Adjustments</h4>
                <table className="w-full text-sm border rounded">
                  <thead className="bg-slate-100">
                    <tr><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Retail Selling Price</th></tr>
                  </thead>
                  <tbody>
                    {reviewAdjustments.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">{item.description || '-'}</td>
                        <td className="px-2 py-1 text-right">{formatCurrency(item.at_retail, currencySymbol)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-medium">
                    <tr className="border-t">
                      <td className="px-2 py-1 text-right">Total:</td>
                      <td className="px-2 py-1 text-right">{formatCurrency(adjustmentsSubtotal.retail, currencySymbol)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ABV Check — Beverages only */}
      {!isFoodScope && reviewAbvChecks.length > 0 && (
        <div className="print:break-inside-avoid" data-testid="review-abv-section">
          <h3 className="font-semibold border-b pb-2 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            ABV Check
          </h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-1 text-left">Product</th>
                <th className="px-2 py-1 text-right">Temp (°C)</th>
                <th className="px-2 py-1 text-right">Density (g/ml)</th>
                <th className="px-2 py-1 text-right">ABV (%)</th>
                <th className="px-2 py-1 text-center">Result</th>
                <th className="px-2 py-1 text-left">Action / Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {reviewAbvChecks.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-2 py-1 font-medium">{item.product || '-'}</td>
                  <td className="px-2 py-1 text-right">{item.temperature ? `${item.temperature}°C` : '-'}</td>
                  <td className="px-2 py-1 text-right">{item.density || '-'}</td>
                  <td className="px-2 py-1 text-right">{item.measured_abv ? `${item.measured_abv}%` : '-'}</td>
                  <td className="px-2 py-1 text-center">
                    {item.pass_fail === true && <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Pass</span>}
                    {item.pass_fail === false && <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Fail</span>}
                    {item.pass_fail == null && '-'}
                  </td>
                  <td className="px-2 py-1 text-xs text-muted-foreground">{item.action_taken || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground italic">
            ABV verification performed using calibrated density measurement equipment. Measurements adjusted for temperature where applicable.
          </p>
        </div>
      )}
      
      {/* Food Allowances — Food scope only */}
      {isFoodScope && reviewFoodAllowances.length > 0 && (
        <div className="print:break-inside-avoid" data-testid="review-food-allowances-section">
          <h3 className="font-semibold border-b pb-2 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            Allowances
          </h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-1 text-left">Description</th>
                <th className="px-2 py-1 text-center">Retail / Cost</th>
                <th className="px-2 py-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {reviewFoodAllowances.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-2 py-1">{item.description || '-'}</td>
                  <td className="px-2 py-1 text-center">
                    {item.value_type === 'retail' && <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Retail</span>}
                    {item.value_type === 'cost' && <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Cost</span>}
                    {!item.value_type && '-'}
                  </td>
                  <td className="px-2 py-1 text-right">{formatCurrency(item.amount, currencySymbol)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100 font-medium">
              <tr className="border-t">
                <td className="px-2 py-1 text-right" colSpan={2}>Total:</td>
                <td className="px-2 py-1 text-right">{formatCurrency(foodAllowancesTotal, currencySymbol)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Observations */}
      {(reviewObservations.key || reviewObservations.actions) && (
        <div className="print:break-inside-avoid">
          <h3 className="font-semibold border-b pb-2 mb-3" style={{ color: ABBEY_COLORS.primary }}>
            Observations
          </h3>
          <div className="space-y-4">
            {reviewObservations.key && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">General Audit Comments</h4>
                <p className="mt-1 whitespace-pre-wrap bg-slate-50 p-3 rounded">{reviewObservations.key}</p>
              </div>
            )}
            {reviewObservations.actions && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Actions Agreed</h4>
                <p className="mt-1 whitespace-pre-wrap bg-slate-50 p-3 rounded">{reviewObservations.actions}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Next Audit block removed 2026-04-28 (iter328) — kept internal
          on Wizard Step 7 for operational scheduling but no longer surfaced
          to clients in the on-screen preview or PDF. */}

      {/* Footer */}
      <div className="border-t pt-4 mt-8 text-center text-xs text-muted-foreground print:mt-12">
        <p>Abbey 5 Star Ltd • Stock Audit Report</p>
        <p>Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
      </div>
      
      {/* Action Buttons (wizard only) */}
      {showActions && canEdit && (
        <div className="flex justify-center gap-4 pt-6 border-t print:hidden">
          <Button variant="outline" onClick={() => onSave?.(false)}
            disabled={saving || !hasUnsavedChanges} data-testid="step7-save-draft-btn">
            {saving ? <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button onClick={onPublish} disabled={publishing}
            className="bg-green-600 hover:bg-green-700" data-testid="step7-publish-btn">
            {publishing ? <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" /> : <Send className="h-4 w-4 mr-2" />}
            Publish Report
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportPreviewContent;
